/**
 * Wait for a set delay and then resolve with the given value. Useful for adding an artificial delay to an async function.
 * @param delay Number of milliseconds to wait before returning the value
 * @param value The value to return
 */
export function waitAndReturn<T>(delay: number, value: T | Promise<T>): Promise<T> {
    return new Promise(resolve => setTimeout(() => resolve(value), delay));
}

interface PromiseRetryOptions {
    /**
     * Number of retries
     */
    retries?: number;
    /**
     * Delay between retries
     */
    retryDelay?: number;
}

/**
 * Returns a promise that retries a given async function a given number of times with a given delay between each retry.
 * @param operation Async function to execute
 * @param {PromiseRetryOptions} options Settings for the retry operation
 * @returns Promise that resolves to the result of the operation
 */
export async function withDelayedRetry<T>(
    operation: () => Promise<T> | T,
    { retries = 3, retryDelay = 500 }: PromiseRetryOptions
): Promise<T> {
    try {
        const result = await operation();
        return result;
    } catch (ex) {
        if (retries > 0) {
            const newRetries = await waitAndReturn(retryDelay, retries - 1);
            return withDelayedRetry(operation, { retryDelay, retries: newRetries });
        }
        return Promise.reject(ex);
    }
}
